/**
 * DriversView - Full-page view for drivers management
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add, Refresh, Search } from "@mui/icons-material";
import { Tag, Space, Table, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { getDrivers } from "../services/driverService";
import { getPartners } from "../../partners/services/partnerService";
import DriverForm from "../components/DriverForm";
import {
  PageHeader,
  PageFilters,
} from "../../../shared/components/pageShell";

const { Text } = Typography;

const matchesDriverSearch = (driver, searchQuery) => {
  if (!searchQuery) return true;
  const searchLower = searchQuery.toLowerCase();
  return (
    driver.name?.toLowerCase().includes(searchLower) ||
    driver.phone?.includes(searchQuery) ||
    driver.licenseNumber?.toLowerCase().includes(searchLower)
  );
};

const normalizeStatusFilter = (value) => {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return undefined;
};

/**
 * DriversView Component
 */
const DriversView = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDriver, setEditDriver] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [apiFilters, setApiFilters] = useState({
    ownerType: "",
    partnerId: "",
    isActive: true,
  });
  const [partners, setPartners] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDrivers(apiFilters);
      if (result.success) {
        setDrivers(result.data || []);
      } else {
        setError(result.error || "Failed to fetch drivers");
        setDrivers([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch drivers");
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [apiFilters]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const result = await getPartners({ isActive: true });
        if (result.success) {
          setPartners(result.data || []);
        }
      } catch (err) {
        console.error("Error loading partners:", err);
      }
    };
    loadPartners();
  }, []);

  const displayedDrivers = useMemo(
    () => drivers.filter((driver) => matchesDriverSearch(driver, searchQuery)),
    [drivers, searchQuery]
  );

  const handleCreateDriver = useCallback(() => {
    setEditDriver(null);
    setFormOpen(true);
  }, []);

  const handleEditDriver = useCallback((driver) => {
    setEditDriver(driver);
    setFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormOpen(false);
    setEditDriver(null);
  }, []);

  const handleDriverSaved = useCallback(() => {
    setFormOpen(false);
    setEditDriver(null);
    fetchDrivers();
    setNotification({
      open: true,
      message: editDriver
        ? "Driver updated successfully"
        : "Driver created successfully",
      severity: "success",
    });
  }, [editDriver, fetchDrivers]);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleApiFilterChange = useCallback((key, value) => {
    setApiFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "ownerType" && value !== "third_party") {
        next.partnerId = "";
      }
      if (key === "isActive") {
        next.isActive = normalizeStatusFilter(value);
      }
      return next;
    });
  }, []);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <Text strong>{name || "N/A"}</Text>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => <Text type="secondary">{phone || "N/A"}</Text>,
    },
    {
      title: "License Number",
      dataIndex: "licenseNumber",
      key: "licenseNumber",
      render: (license) => <Text code>{license || "N/A"}</Text>,
    },
    {
      title: "License Expiry",
      dataIndex: "licenseExpiry",
      key: "licenseExpiry",
      render: (expiry) => {
        if (!expiry) return "N/A";
        const date = new Date(expiry);
        const isExpired = date < new Date();
        return (
          <Text type={isExpired ? "danger" : "secondary"}>
            {date.toLocaleDateString()}
            {isExpired && (
              <Tag color="red" style={{ marginLeft: 8 }}>
                Expired
              </Tag>
            )}
          </Text>
        );
      },
    },
    {
      title: "Owner Type",
      dataIndex: "ownerType",
      key: "ownerType",
      render: (type, record) => (
        <Space>
          <Tag color={type === "bubakii" ? "blue" : "orange"}>
            {type === "bubakii" ? "Bubakii" : "3rd Party"}
          </Tag>
          {type === "third_party" && record.partner?.name && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              ({record.partner.name})
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditDriver(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title="Drivers"
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateDriver}
            sx={{
              width: { xs: "100%", sm: "auto" },
              flex: { xs: 1, md: "none" },
              textTransform: "none",
              fontWeight: 600,
              minHeight: { xs: 44, md: 36 },
            }}
          >
            Add Driver
          </Button>
        }
      />

      <PageFilters>
        <TextField
          placeholder="Search drivers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: { xs: "100%", sm: 250 },
            flex: { sm: 1 },
            bgcolor: "background.paper",
          }}
        />
        <FormControl
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 150 }, bgcolor: "background.paper" }}
        >
          <InputLabel>Owner Type</InputLabel>
          <Select
            value={apiFilters.ownerType}
            label="Owner Type"
            onChange={(e) => handleApiFilterChange("ownerType", e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="bubakii">Bubakii</MenuItem>
            <MenuItem value="third_party">3rd Party</MenuItem>
          </Select>
        </FormControl>
        {apiFilters.ownerType === "third_party" && (
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              bgcolor: "background.paper",
            }}
          >
            <InputLabel>Partner</InputLabel>
            <Select
              value={apiFilters.partnerId}
              label="Partner"
              onChange={(e) =>
                handleApiFilterChange("partnerId", e.target.value)
              }
            >
              <MenuItem value="">All Partners</MenuItem>
              {partners.map((partner) => (
                <MenuItem key={partner.id} value={partner.id}>
                  {partner.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 150 }, bgcolor: "background.paper" }}
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={
              apiFilters.isActive === undefined ? "" : apiFilters.isActive
            }
            label="Status"
            onChange={(e) => handleApiFilterChange("isActive", e.target.value)}
          >
            <MenuItem value={true}>Active</MenuItem>
            <MenuItem value={false}>Inactive</MenuItem>
            <MenuItem value="">All</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchDrivers}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Refresh
        </Button>
      </PageFilters>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={displayedDrivers}
            rowKey="id"
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} drivers`,
            }}
          />
        </Box>
      )}

      <DriverForm
        open={formOpen}
        driver={editDriver}
        onClose={handleCloseForm}
        onSave={handleDriverSaved}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DriversView;
